/**
 * MyMemberships.jsx — iter-106 (P3)
 *
 * Self-cleanup page where users can see every band they belong to (with
 * last-activity stamps) and leave the ones they're no longer part of.
 *
 * Backend powering this page:
 *   GET  /api/bands/my            → enriched membership list (iter-106)
 *   POST /api/bands/leave/{id}    → leave a band
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Building2, ShieldCheck, Users, LogOut, RefreshCw, AlertTriangle, CheckCircle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { apiClient } from "../lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return `${format(new Date(iso), "MMM d, yyyy")} (${formatDistanceToNow(new Date(iso), { addSuffix: true })})`;
  } catch {
    return iso;
  }
};

export default function MyMemberships() {
  const { user } = useAuth();
  const [bands, setBands] = useState([]);
  const [canLeaveAny, setCanLeaveAny] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leavingId, setLeavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/bands/my");
      setBands(r.data?.bands || []);
      setCanLeaveAny(!!r.data?.can_leave_any);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load memberships");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const leaveBand = async (band) => {
    if (band.is_current) {
      toast.warning("Switch active band before leaving this one (Profile → switch).");
      return;
    }
    if (band.is_last_admin) {
      toast.error("You're the last admin of this band — promote another admin first.");
      return;
    }
    if (!window.confirm(`Leave "${band.band_name}"? You'll lose access to its events, chat, and inventory. This can be reversed by an admin re-inviting you.`)) return;
    setLeavingId(band.band_id);
    try {
      await apiClient.post(`/bands/leave/${band.band_id}`);
      toast.success(`Left "${band.band_name}".`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't leave band");
    } finally {
      setLeavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" /> My Memberships
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Hi {user?.name?.split(" ")[0] || "there"} — review every band you belong to and leave the ones you no longer need.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="memberships-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading && (
        <Card className="glass-card"><CardContent className="py-12 text-center text-muted-foreground">Loading…</CardContent></Card>
      )}

      {!loading && bands.length === 0 && (
        <Card className="glass-card"><CardContent className="py-12 text-center text-muted-foreground">
          You're not a member of any bands.
        </CardContent></Card>
      )}

      {!loading && bands.length > 0 && !canLeaveAny && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 flex items-start gap-2" data-testid="single-band-notice">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>This is your only band.</strong> You can't leave it — if it's no longer needed, an admin can delete the band entirely from Band Settings → Danger Zone.
          </div>
        </div>
      )}

      {!loading && bands.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {bands.map((b) => (
            <Card key={b.band_id} className="glass-card" data-testid={`membership-${b.band_id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between gap-2 text-lg">
                  <span className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-5 h-5 text-primary shrink-0" />
                    <span className="truncate" title={b.band_name}>{b.band_name}</span>
                  </span>
                  {b.is_current && (
                    <Badge className="bg-primary/20 text-primary border-primary/30 shrink-0">Active</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  {b.role === "admin" ? (
                    <Badge className="bg-primary/20 text-primary"><ShieldCheck className="w-3 h-3 mr-1" />Admin</Badge>
                  ) : (
                    <Badge variant="outline">{b.role}</Badge>
                  )}
                  {b.subscription_status && (
                    <Badge variant="outline" className="text-muted-foreground">{b.subscription_status}</Badge>
                  )}
                  {b.band_type && (
                    <Badge variant="outline" className="text-muted-foreground">{b.band_type.replace(/_/g, " ")}</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Last activity:</span> {fmt(b.last_activity_at)}
                </div>
                {b.is_last_admin && (
                  <div className="text-[11px] text-amber-300 flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    You're the last admin here — leaving is blocked until another admin is appointed.
                  </div>
                )}
                <div className="pt-1">
                  {b.is_current ? (
                    <div className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400" />
                      This is your active band. Switch to another band first if you want to leave this one.
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                      disabled={leavingId === b.band_id || !canLeaveAny || b.is_last_admin}
                      onClick={() => leaveBand(b)}
                      data-testid={`leave-band-${b.band_id}`}
                    >
                      <LogOut className="w-3.5 h-3.5 mr-1.5" />
                      {leavingId === b.band_id ? "Leaving…" : "Leave band"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
